import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { AIRMASK_GREEN, AIRMASK_DARK, AIRMASK_SURFACE_GRAY } from '../constants/colors';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { fileSystemService } from '../services/api';

interface MarkdownTextProps {
  children: string;
  sx?: any;
  onCopy?: (e: React.ClipboardEvent) => void;
}

export const markdownHtmlSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'details', 'summary'],
  attributes: {
    ...defaultSchema.attributes,
    details: [],
    summary: [],
  },
};

/**
 * Shared link renderer component for ReactMarkdown.
 * Opens links in a new tab with security attributes.
 */
export const markdownLinkComponent = {
  a: ({ node, children, ...props }: any) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children ?? 'Link'}
    </a>
  ),
};

const MarkdownSummaryComponent = ({ children, ...props }: any) => {
  const summaryRef = useRef<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const summary = summaryRef.current;
    if (!summary) {
      return undefined;
    }

    const details = summary.parentElement as HTMLDetailsElement | null;
    if (!details) {
      return undefined;
    }

    const syncOpenState = () => setIsExpanded(details.open);
    syncOpenState();
    details.addEventListener('toggle', syncOpenState);

    return () => details.removeEventListener('toggle', syncOpenState);
  }, []);

  return (
    <summary
      {...props}
      ref={summaryRef}
      style={{
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        color: AIRMASK_GREEN,
        fontFamily: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
      }}
    >
      <span
        aria-hidden="true"
        data-airmask-summary-marker="true"
        style={{
          display: 'inline-block',
          width: '0.8em',
          height: '0.8em',
          color: AIRMASK_GREEN,
          backgroundColor: AIRMASK_GREEN,
          clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 120ms ease',
          flexShrink: 0,
          transformOrigin: 'center',
        }}
      />
      <span>{children}</span>
    </summary>
  );
};

const SHOW_TAG_REGEX = /<show\s+file=(?:"([^"]+)"|'([^']+)'|\{([^}]+)\}|([^\s/>]+))\s*\/?>/g;

const stripFirstH1 = (markdown: string): string => {
  const lines = markdown.split(/\r?\n/);
  const firstH1Index = lines.findIndex((line) => /^#\s+/.test(line.trim()));

  if (firstH1Index === -1) {
    return markdown;
  }

  return lines.filter((_, index) => index !== firstH1Index).join('\n');
};

const isAirMaskFileUrl = (value: string): boolean => {
  if (!value) {
    return false;
  }

  if (value.startsWith('/api/files/')) {
    return true;
  }

  try {
    const parsed = new URL(value, window.location.origin);
    return parsed.origin === window.location.origin && /^\/api\/files\/[^/]+(?:\/content)?$/.test(parsed.pathname);
  } catch {
    return false;
  }
};

const extractFileIdFromAirMaskUrl = (value: string): string | null => {
  if (!isAirMaskFileUrl(value)) {
    return null;
  }

  const parsed = new URL(value, window.location.origin);
  const match = parsed.pathname.match(/^\/api\/files\/([^/]+)(?:\/content)?$/);
  return match ? match[1] : null;
};

export const expandShowTags = async (markdown: string): Promise<string> => {
  const matches = Array.from(markdown.matchAll(SHOW_TAG_REGEX));

  if (matches.length === 0) {
    return markdown;
  }

  let resolved = markdown;

  for (const match of matches) {
    const tagValue = match[1] ?? match[2] ?? match[3] ?? match[4] ?? '';
    const fileId = extractFileIdFromAirMaskUrl(tagValue);

    if (!fileId) {
      resolved = resolved.replace(match[0], '');
      continue;
    }

    const response = await fileSystemService.getFile(fileId);
    if (!response.success || !response.data?.content) {
      resolved = resolved.replace(match[0], '');
      continue;
    }

    const insertedMarkdown = stripFirstH1(response.data.content);
    resolved = resolved.replace(match[0], insertedMarkdown);
  }

  return resolved;
};

interface MarkdownRendererProps {
  children: string;
  components?: Record<string, any>;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ children, components = {} }) => {
  const [resolvedChildren, setResolvedChildren] = useState(children);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const expanded = await expandShowTags(children);
      if (!cancelled) {
        setResolvedChildren(expanded);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [children]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownHtmlSchema]]}
      components={{ ...markdownLinkComponent, summary: MarkdownSummaryComponent, ...components }}
    >
      {resolvedChildren}
    </ReactMarkdown>
  );
};

/**
 * MarkdownText - A component for rendering markdown-formatted text in checklist items.
 * 
 * Supports:
 * - **Bold text** using **text**
 * - *Italic text* using *text*
 * - `Inline code` using `code`
 * - [Links](url) using [text](url) - opens in new tab
 * - Lists using - or 1.
 * - Multi-line text with proper line breaks
 * - Raw HTML allowlisted for <details> and <summary>
 * 
 * Used in ChecklistExpand and DateChecklistExpand components for rendering item text.
 */
const MarkdownText: React.FC<MarkdownTextProps> = ({ children, sx, onCopy }) => {
  // Split on blank lines (2+ consecutive newlines) so that:
  //  - Each chunk is rendered independently by ReactMarkdown, preserving correct
  //    block structure (lists don't absorb following paragraphs via lazy continuation).
  //  - A visible spacer <Box> is inserted between chunks, giving the same blank-line
  //    appearance as the textarea edit view.
  // Within each chunk, remark-breaks converts single \n → <br>.
  const chunks = children.split(/\n\n+/);

  return (
    <Box
      onCopy={onCopy}
      sx={{
        flexGrow: 1,
        minWidth: 0,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        '& p': { m: 0, mb: 0.5 },
        '& ul, & ol': { pl: 2, mb: 0.5, mt: 0 },
        // Explicitly set disc so bullets aren't treated as nested (hollow) by the
        // browser due to this <ul> living inside a MUI ListItem <li>.
        '& ul': { listStyleType: 'disc' },
        '& li': { mb: 0.25 },
        '& details': { my: 1 },
        '& summary': {
          cursor: 'pointer',
          fontWeight: 600,
          color: AIRMASK_GREEN,
          listStyle: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          '&::-webkit-details-marker': {
            display: 'none',
          },
          '&::marker': {
            display: 'none',
          },
        },
        '& summary [data-airmask-summary-marker="true"]': {
          backgroundColor: AIRMASK_GREEN,
          clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          mb: 2,
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          p: 1,
          textAlign: 'left',
        },
        '& th': {
          bgcolor: AIRMASK_GREEN,
          color: AIRMASK_DARK,
          fontWeight: 600,
        },
        '& code': { 
          bgcolor: AIRMASK_SURFACE_GRAY,
          color: 'text.primary',
          px: 0.5, 
          borderRadius: 0.5,
          fontSize: '0.875em'
        },
        '& pre': { 
          bgcolor: AIRMASK_SURFACE_GRAY,
          color: 'text.primary',
          p: 1, 
          borderRadius: 1,
          overflow: 'auto'
        },
        '& strong': { fontWeight: 600 },
        '& em': { fontStyle: 'italic' },
        '& a': { color: 'primary.main', textDecoration: 'underline', wordBreak: 'break-all' },
        ...sx,
      }}
    >
      {chunks.map((chunk, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Box sx={{ height: '1em' }} />}
          <MarkdownRenderer>{chunk}</MarkdownRenderer>
        </React.Fragment>
      ))}
    </Box>
  );
};

export default MarkdownText;
